import { Component, computed, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import {
  LunchConfiguration,
  LunchResponsibility,
  Weekday,
  calculateLunchFee,
  ensureUrlProtocol,
} from '@awo-care/shared-core';

@Component({
  selector: 'app-lunch-select',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <section
      class="lunch-select"
      data-testid="lunch-select"
      role="group"
      aria-labelledby="lunch-heading"
    >
      <h3 id="lunch-heading" i18n="@@registration.lunch.title">Mittagessen</h3>

      @if (showExternalNotice()) {
        <div class="external-notice" data-testid="lunch-external-notice">
          <mat-icon class="material-icons-outlined">info</mat-icon>
          <div>
            <div class="external-notice-title" i18n="@@registration.lunch.notAwo.title">
              Das Mittagessen läuft nicht über die AWO
            </div>
            <div i18n="@@registration.lunch.notAwo.body">
              An dieser Schule organisiert die AWO kein Mittagessen. Anmeldung,
              Abmeldung und Abrechnung laufen direkt über die Schule bzw. den Caterer.
            </div>
            @if (effectiveExternalInfo(); as info) {
              @if (info.url) {
                <a
                  [href]="info.url"
                  target="_blank"
                  rel="noopener"
                  class="external-link"
                  data-testid="lunch-external-notice-link"
                >
                  <mat-icon class="material-icons-outlined">open_in_new</mat-icon>
                  <span i18n="@@registration.lunch.external.link">Mehr Informationen</span>
                </a>
              }
            }
          </div>
        </div>
      }

      @if (!infoOnlyMode()) {
        <div class="cards">
          @for (c of selectableConfigurations(); track c.id) {
            <button
              type="button"
              class="card lunch-item"
              [attr.data-testid]="'lunch-item-' + c.id"
              [class.selected]="selectedConfigId() === c.id"
              [attr.aria-pressed]="selectedConfigId() === c.id"
              (click)="onSelect(c)"
            >
              <mat-icon class="card-icon">{{ iconFor(c.type) }}</mat-icon>
              <div class="card-content">
                <div class="card-title">{{ c.name }}</div>
                <div class="card-desc">{{ subtitleFor(c) }}</div>
              </div>
              <div class="card-price">{{ priceFor(c) }}</div>
            </button>
          }

          @if (!mandatoryLunchId()) {
            <button
              type="button"
              class="card lunch-item lunch-item-none"
              data-testid="lunch-item-none"
              [class.selected]="selectedConfigId() === null && selectionMade()"
              (click)="onSelectNone()"
            >
              <mat-icon class="card-icon dim">no_meals</mat-icon>
              <div class="card-content">
                <div class="card-title" i18n="@@registration.lunch.without">Kein Mittagessen</div>
                <div class="card-desc" i18n="@@registration.lunch.withoutHint">
                  Das Kind isst anderweitig
                </div>
              </div>
              <div class="card-price">0 €/Mo</div>
            </button>
          }
        </div>
      }

      @if (selectedConfig()?.type === 'DAILY_SELECTION') {
        <div class="daily-picker" data-testid="lunch-daily-picker">
          <div class="info" [class.warn]="!isMinDaysOk()">
            <mat-icon>info</mat-icon>
            <span>
              @if (selectedConfig()?.minDays) {
                <ng-container i18n="@@registration.lunch.minDaysHint">Mindestens</ng-container>
                <strong>{{ selectedConfig()?.minDays }}</strong>
                <ng-container i18n="@@registration.lunch.minDaysHint2"
                  >Tag(e) erforderlich.</ng-container
                >
              } @else {
                <ng-container i18n="@@registration.lunch.dailyHint"
                  >Wählen Sie die Tage, an denen Ihr Kind Mittagessen möchte.</ng-container
                >
              }
            </span>
          </div>
          <div class="day-row">
            @for (day of selectableDays(); track day) {
              <button
                type="button"
                class="day-item"
                [class.selected]="isDaySelected(day)"
                (click)="toggleDay(day)"
              >
                <span class="day-name">{{ dayLabel(day) }}</span>
                <span class="day-price">{{ selectedConfig()?.fee?.amount ?? 0 }} €</span>
              </button>
            }
          </div>
          <div class="daily-total">
            <span i18n="@@registration.lunch.total">Mittagessen gesamt:</span>
            <strong>{{ totalDailyFee() | number: '1.2-2' }} €/Monat</strong>
          </div>
        </div>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .lunch-select {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }
      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: bold;
      }
      .cards {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      .card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        border: 2px solid rgba(0, 0, 0, 0.12);
        border-radius: 12px;
        background: white;
        text-align: left;
        cursor: pointer;
        font: inherit;
      }
      .card.selected {
        border-color: var(--primary-color);
        background: rgba(var(--primary-color-rgb, 226, 56, 42), 0.04);
      }
      .card-icon {
        color: var(--primary-color);
      }
      .card-icon.dim {
        color: rgba(0, 0, 0, 0.4);
      }
      .card-content {
        flex: 1;
        min-width: 0;
      }
      .card-title {
        font-weight: 500;
      }
      .card-desc {
        font-size: 13px;
        color: rgba(0, 0, 0, 0.6);
      }
      .card-price {
        font-weight: 500;
        color: var(--primary-color);
        white-space: nowrap;
      }
      .external-notice {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 0.875rem 1rem;
        border-radius: 8px;
        background: #fff5e9;
        border: 1px solid #f3ddbf;
        font-size: 14px;
        line-height: 1.5;
      }
      .external-notice mat-icon {
        color: #ef6c00;
        flex: 0 0 auto;
      }
      .external-notice-title {
        font-weight: 600;
        margin-bottom: 0.25rem;
      }
      .external-link {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        margin-top: 0.5rem;
        color: inherit;
        text-decoration: underline;
      }
      .external-link mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }

      .daily-picker {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        padding-top: 0.5rem;
      }
      .info {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        padding: 0.75rem;
        background: rgba(8, 172, 211, 0.08);
        border-left: 3px solid #08acd3;
        border-radius: 4px;
        font-size: 13px;
        color: #005f73;
      }
      .info.warn {
        background: #fff3cd;
        border-left-color: #856404;
        color: #856404;
      }
      .day-row {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .day-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem;
        border: 2px solid rgba(0, 0, 0, 0.12);
        border-radius: 8px;
        background: white;
        cursor: pointer;
        font: inherit;
      }
      .day-item.selected {
        border-color: var(--primary-color);
        background: rgba(var(--primary-color-rgb, 226, 56, 42), 0.04);
      }
      .daily-total {
        display: flex;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        background: rgba(82, 122, 0, 0.06);
        border: 1px solid rgba(82, 122, 0, 0.2);
        border-radius: 8px;
        color: #527a00;
        font-size: 14px;
      }
    `,
  ],
})
export class LunchSelectComponent {
  availableConfigurations = input.required<LunchConfiguration[]>();
  selectableDays = input.required<Weekday[]>();
  mandatoryLunchId = input<string | null>(null);
  initialConfigId = input<string | null>(null);
  initialSelectedDays = input<Weekday[]>([]);
  /**
   * Zuständigkeit fürs Essen (#748). Ohne expliziten Wert aus den übergebenen
   * Konfigurationen abgeleitet, damit Bestands-Consumer (Modulwechsel,
   * Mitarbeiter-Dialog) denselben Hinweis zeigen wie die Anmeldung.
   */
  responsibility = input<LunchResponsibility | null>(null);
  externalInfo = input<{ description: string; url?: string } | null>(null);

  selectionChange = output<{ configId: string | null; selectedDays: Weekday[] }>();

  selectedConfigId = signal<string | null>(null);
  selectedDays = signal<Weekday[]>([]);
  selectionMade = signal(false);

  selectableConfigurations = computed<LunchConfiguration[]>(() =>
    this.availableConfigurations().filter((c) => c.type !== 'EXTERNAL'),
  );

  externalInfoConfigs = computed<LunchConfiguration[]>(() =>
    this.availableConfigurations().filter((c) => c.type === 'EXTERNAL'),
  );

  private effectiveResponsibility = computed<LunchResponsibility>(() => {
    const explicit = this.responsibility();
    if (explicit) return explicit;
    return this.selectableConfigurations().length > 0 ? 'AWO' : 'EXTERNAL';
  });

  showExternalNotice = computed(() => this.effectiveResponsibility() === 'EXTERNAL');

  effectiveExternalInfo = computed<{ description: string; url?: string } | null>(() => {
    const explicit = this.externalInfo();
    if (explicit) return explicit;
    const ext = this.externalInfoConfigs().find((c) => !!c.externalInfo);
    if (!ext?.externalInfo) return null;
    const url = ensureUrlProtocol(ext.externalInfo.url);
    return { description: ext.externalInfo.description, ...(url ? { url } : {}) };
  });

  /**
   * Info-Only Modus: Die AWO organisiert kein Essen — Picker zeigt nur den
   * Hinweis, keine Auswahl-Karten; die Auswahl wird implizit auf
   * "kein Mittagessen" gesetzt.
   */
  infoOnlyMode = computed(
    () => this.showExternalNotice() && !this.mandatoryLunchId(),
  );

  selectedConfig = computed<LunchConfiguration | null>(() => {
    const id = this.selectedConfigId();
    if (!id) return null;
    return this.availableConfigurations().find((c) => c.id === id) ?? null;
  });

  totalDailyFee = computed(() => {
    const c = this.selectedConfig();
    if (!c) return 0;
    return calculateLunchFee(c, this.selectedDays());
  });

  isMinDaysOk = computed(() => {
    const c = this.selectedConfig();
    if (!c || c.type !== 'DAILY_SELECTION') return true;
    const min = c.minDays ?? 0;
    return this.selectedDays().length >= min;
  });

  constructor() {
    queueMicrotask(() => {
      const mandatory = this.mandatoryLunchId();
      if (mandatory) {
        this.selectedConfigId.set(mandatory);
        this.selectionMade.set(true);
      } else if (this.infoOnlyMode()) {
        this.selectedConfigId.set(null);
        this.selectionMade.set(true);
        this.emit();
      } else {
        const initial = this.initialConfigId();
        const initialConfig = initial
          ? this.availableConfigurations().find((c) => c.id === initial)
          : null;
        if (initial && initialConfig?.type !== 'EXTERNAL') {
          this.selectedConfigId.set(initial);
          this.selectionMade.set(true);
        }
      }
      this.selectedDays.set(this.initialSelectedDays());
    });
  }

  onSelect(c: LunchConfiguration) {
    if (c.type === 'EXTERNAL') return;
    this.selectedConfigId.set(c.id);
    this.selectionMade.set(true);
    if (c.type !== 'DAILY_SELECTION') {
      this.selectedDays.set([]);
    }
    this.emit();
  }

  onSelectNone() {
    if (this.mandatoryLunchId()) return;
    this.selectedConfigId.set(null);
    this.selectedDays.set([]);
    this.selectionMade.set(true);
    this.emit();
  }

  toggleDay(day: Weekday) {
    const cur = this.selectedDays();
    this.selectedDays.set(cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]);
    this.emit();
  }

  isDaySelected(day: Weekday): boolean {
    return this.selectedDays().includes(day);
  }

  private emit() {
    this.selectionChange.emit({
      configId: this.selectedConfigId(),
      selectedDays: this.selectedDays(),
    });
  }

  iconFor(t: LunchConfiguration['type']): string {
    if (t === 'DAILY_SELECTION') return 'today';
    return 'lunch_dining';
  }

  subtitleFor(c: LunchConfiguration): string {
    if (c.type === 'DAILY_SELECTION')
      return $localize`:@@registration.lunch.dailyDesc:Sie wählen unten die einzelnen Tage`;
    return $localize`:@@registration.lunch.fixedDesc:Festpreis pro Monat`;
  }

  priceFor(c: LunchConfiguration): string {
    if (c.type === 'DAILY_SELECTION') return `+ ${c.fee?.amount ?? 0} €/Mo je Wochentag`;
    return `+ ${c.fee?.amount ?? 0} €/Mo`;
  }

  dayLabel(d: Weekday): string {
    return { Mo: 'Montag', Di: 'Dienstag', Mi: 'Mittwoch', Do: 'Donnerstag', Fr: 'Freitag' }[d];
  }
}
